import React, { useState } from 'react';
import Excel from 'exceljs';
import PropTypes from 'prop-types';
import { Link } from '@ipc/ipc-core';
import { Button, Input } from '@ipc/ipc-components';

import SecureComponent from '../../components/custom/SecureComponent';
import AlertDialog from '../../components/custom/AlertDialog';
import { FormLabel } from '../../elements';
import {
  TopButtonsWrapper, StyledTextarea, InputFileWrapper, AppContentInner, AppContentTitle,
} from '../../styles';
import { getMDTemplate, createMasterData } from '../../api';
import { formatDate } from '../../utils/date';
import { primaryButton } from '../../styles/themes';
import {
  hasAccess, ROLE_ADMIN, ROLE_CARRIER, ROLE_OPERATOR,
} from '../../utils/access';

import AdminTemplate from './templates/template-admin.xlsx';
import CarrierTemplate from './templates/template-carrier.xlsx';
import OperatorTemplate from './templates/template-operator.xlsx';
import { useMasterData } from './hooks';
import { DownloadButtonWrapper, StyledTable } from './styles';
import { TABLE_COLUMNS, FIELDS_MAPPING } from './constants';

const MaserData = ({ newSuccess, newWarning }) => {
  const [data, loading, deleteItem, reloadData] = useMasterData();
  const [values, updateValues] = useState({});
  const [submitted, updateSubmitted] = useState(false);
  const [modal, updateModal] = useState({
    open: false,
    confirmTitle: 'Update',
    data: {},
  });
  const downloadTemplateHandler = async () => {
    let filePath = '';
    if (hasAccess([ROLE_ADMIN])) {
      filePath = AdminTemplate;
    } else if (hasAccess([ROLE_CARRIER])) {
      filePath = CarrierTemplate;
    } else if (hasAccess([ROLE_OPERATOR])) {
      filePath = OperatorTemplate;
    }

    await getMDTemplate(filePath);
  };

  const modalConfirmHandler = () => {
    updateSubmitted(true);

    if (!values.name || !values.file || !values.description) {
      return;
    }

    const wb = new Excel.Workbook();
    const reader = new FileReader();
    const receptacles = [];

    reader.readAsArrayBuffer(values.file);
    reader.onload = async () => {
      const buffer = reader.result;
      const workbook = await wb.xlsx.load(buffer).catch(() => {
        throw newWarning('Invalid file');
      });
      let properties = [];

      if (!workbook) {
        return;
      }
      workbook.eachSheet((sheet) => {
        if (sheet.rowCount < 2) {
          throw newWarning('File content should not be empty', 8);
        }
        sheet.eachRow((row, rowIndex) => {
          if (rowIndex === 1) {
            properties = row.values.map((item = '') => FIELDS_MAPPING[item.trim()]);
          } else {
            if (row.values.length < 11) {
              throw newWarning('All fields should be filled in', 8);
            }
            const receptacle = {};
            row.values.map((item, index) => {
              const columnTitle = properties[index];
              if (columnTitle) {
                receptacle[columnTitle] = typeof item === 'object' ? formatDate(item) : item;
              }

              return item;
            });
            receptacles.push(receptacle);
          }
        });
      });

      const body = {
        fileName: values.file.name,
        name: values.name,
        description: values.description,
        receptacles: receptacles.map(receptacle => ({
          ...receptacle,
          dateType: {
            label: receptacle.dateType,
          },
          mailClass: {
            label: receptacle.mailClass,
          },
          carrier: {
            label: receptacle.carrier,
          },
          postalOperator: {
            label: receptacle.postalOperator,
          },
        })),
      };
      const response = await createMasterData(body);
      if (response) {
        await reloadData();
        newSuccess('Receptacles were uploaded with success!', 5);
      }

      updateModal({
        ...modal,
        open: false,
      });
      updateValues({});
      updateSubmitted(false);
    };
  };

  const modalDismissHandler = () => {
    updateModal({
      ...modal,
      open: false,
    });
    updateValues({});
    updateSubmitted(false);
  };

  const fileChangeHandler = (event) => {
    let file;

    const uploadedFile = event.target.files[0];
    if (uploadedFile && uploadedFile.name) {
      const fileExtension = uploadedFile.name.split('.');
      if (!['xlsx', 'xls'].includes(fileExtension[fileExtension.length - 1])) {
        newWarning('File format is not consistent with template');
        return;
      }
    }

    if (event.target.files && event.target.files.length) {
      [file] = event.target.files;
    }
    updateValues({
      ...values,
      file,
      fileValue: event.target.value,
    });
  };
  const inputChangeHandler = ({ target: { value, name } }) => {
    updateValues({
      ...values,
      [name]: value,
    });
  };

  const openModalHandler = () => {
    updateModal({
      ...modal,
      open: true,
    });
  };

  const actions = item => (
    <>
      <Link to={`/master-data/${item.id}`}>View</Link>
      <SecureComponent roles={[ROLE_ADMIN]}>
        <div onClick={deleteItem(item.id)} data-testid="delete-action">Delete</div>
      </SecureComponent>
    </>
  );

  return (
    <AppContentInner>
      <AppContentTitle>Upload Receptacle Data</AppContentTitle>

      <AlertDialog
        title="Upload Message File"
        open={modal.open}
        confirmTitle="Upload"
        onConfirmation={modalConfirmHandler}
        onDismiss={modalDismissHandler}
      >
        <div className="FormRowInline">
          <FormLabel title="Name" labelAbove required hasError={submitted && !values.name}>
            <InputFileWrapper>
              <Input value={values.name} onChange={inputChangeHandler} name="name" data-testid="file-name" />
            </InputFileWrapper>
          </FormLabel>

          <FormLabel title="Upload File" labelAbove required hasError={submitted && !values.file}>
            <InputFileWrapper>
              <Input type="file" value={values.fileValue} onChange={fileChangeHandler} data-testid="file-to-upload" accept=".xlsx,.xls" />
            </InputFileWrapper>
          </FormLabel>

          <FormLabel title="Description" labelAbove required hasError={submitted && !values.description}>
            <StyledTextarea onChange={inputChangeHandler} name="description" value={values.description} data-testid="file-description" />
          </FormLabel>
        </div>
      </AlertDialog>

      <TopButtonsWrapper>
        <Button onClick={openModalHandler} theme={primaryButton} data-testid="upload-file">Upload Message File</Button>
        <DownloadButtonWrapper>
          <Button onClick={downloadTemplateHandler} data-testid="download-template">Download Template</Button>
        </DownloadButtonWrapper>
      </TopButtonsWrapper>
      <StyledTable
        columns={TABLE_COLUMNS}
        data={data}
        noSelect
        loading={loading}
        actions={actions}
      />
    </AppContentInner>
  );
};


MaserData.propTypes = {
  newSuccess: PropTypes.func,
  newWarning: PropTypes.func,
};

export default MaserData;
